import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import Attachments from "@/components/Attachments";
import PaymentHistory from "@/components/PaymentHistory";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import GstPortalLaunch, { isGstTask } from "@/components/clients/GstPortalLaunch";

export default function TaskDialog({ open, onOpenChange, clients, employees, services, initial, onSaved }) {
  const isEdit = !!initial;
  const { user } = useAuth();
  const [vault, setVault] = useState(null);
  useEffect(() => {
    if (open && isEdit && isGstTask(initial) && !vault) api.get("/gst-vault/status").then(r => setVault(r.data)).catch(() => {});
  }, [open, isEdit]); // eslint-disable-line
  const buildForm = () => ({
    priority: initial?.priority || "Medium",
    client_id: initial?.client_id || (clients[0]?.id ?? ""),
    new_client_name: "",
    new_client_phone: "",
    service: initial?.service || services[0] || "",
    description: initial?.description || "",
    assigned_employee_id: initial?.assigned_employee_id || "",
    deadline: initial?.deadline?.slice(0, 16) || new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 16),
    documents_required: (initial?.documents_required || []).join(", "),
    documents_pending: (initial?.documents_pending || []).join(", "),
    payment_status: initial?.payment_status || "Pending",
    remarks: initial?.remarks || "",
  });
  const [form, setForm] = useState(buildForm);
  const [busy, setBusy] = useState(false);

  // Re-sync form whenever the dialog opens with a different task OR
  // the parent finishes loading catalogs (clients/services).
  useEffect(() => {
    if (open) setForm(buildForm());
  }, [open, initial?.id, clients.length, services.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async () => {
    const isNewClient = form.client_id === "__new__";
    if (isNewClient && !form.new_client_name.trim()) { toast.error("Type the new client's name"); return; }
    setBusy(true);
    try {
      const payload = {
        priority: form.priority,
        client_id: isNewClient ? null : form.client_id,
        new_client: isNewClient ? { name: form.new_client_name.trim(), phone: form.new_client_phone.trim() } : null,
        service: form.service,
        description: form.description,
        assigned_employee_id: form.assigned_employee_id || null,
        deadline: new Date(form.deadline).toISOString(),
        documents_required: form.documents_required.split(",").map(s => s.trim()).filter(Boolean),
        documents_pending: form.documents_pending.split(",").map(s => s.trim()).filter(Boolean),
        payment_status: form.payment_status,
        remarks: form.remarks,
      };
      if (isEdit) {
        await api.patch(`/tasks/${initial.id}`, payload);
        toast.success("Task updated");
      } else {
        await api.post("/tasks", payload);
        toast.success("Task created", { description: isNewClient ? `New client "${form.new_client_name.trim()}" added to your Clients` : "Auto-assigned by specialization if no assignee chosen" });
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold tracking-tight text-2xl">{isEdit ? "Edit Task" : "New Task"}</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">Assign work by service — the least-loaded specialist is picked automatically.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className={form.client_id === "__new__" ? "col-span-2 grid grid-cols-3 gap-3" : ""}>
            <div>
              <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Client</Label>
              <Select value={form.client_id} onValueChange={upd("client_id")} disabled={isEdit}>
                <SelectTrigger data-testid="task-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {!isEdit ? <SelectItem value="__new__" data-testid="task-client-new-option" className="font-semibold">+ New client (type name)</SelectItem> : null}
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.client_id === "__new__" ? (<>
              <div>
                <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">New client name</Label>
                <Input data-testid="task-new-client-name" autoFocus value={form.new_client_name} onChange={upd("new_client_name")} placeholder="e.g. Sharma Traders" />
              </div>
              <div>
                <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Phone (optional)</Label>
                <Input data-testid="task-new-client-phone" value={form.new_client_phone} onChange={upd("new_client_phone")} placeholder="10-digit mobile" />
              </div>
            </>) : null}
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Service</Label>
            <Select value={form.service} onValueChange={upd("service")}>
              <SelectTrigger data-testid="task-service-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">
                {services.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Priority</Label>
            <Select value={form.priority} onValueChange={upd("priority")}>
              <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["High", "Medium", "Low"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Deadline</Label>
            <Input data-testid="task-deadline-input" type="datetime-local" value={form.deadline} onChange={upd("deadline")} />
          </div>
          <div className="col-span-2">
            <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Assignee (leave blank to auto-assign)</Label>
            <Select value={form.assigned_employee_id || "__auto__"} onValueChange={(v) => upd("assigned_employee_id")(v === "__auto__" ? "" : v)}>
              <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Auto by specialization" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__auto__">Auto — pick best specialist</SelectItem>
                {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name} · {e.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Description</Label>
            <Textarea data-testid="task-description-input" rows={2} value={form.description} onChange={upd("description")} />
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Docs required (comma-separated)</Label>
            <Input data-testid="task-docs-required-input" value={form.documents_required} onChange={upd("documents_required")} />
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Docs pending (comma-separated)</Label>
            <Input data-testid="task-docs-pending-input" value={form.documents_pending} onChange={upd("documents_pending")} />
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Payment</Label>
            <Select value={form.payment_status} onValueChange={upd("payment_status")}>
              <SelectTrigger data-testid="task-payment-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Pending">Pending</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Remarks</Label>
            <Input data-testid="task-remarks-input" value={form.remarks} onChange={upd("remarks")} />
          </div>
        </div>

        {isEdit ? (
          <>
            {vault?.allowed && isGstTask(initial) ? (
              <div className="mt-4 border border-zinc-900 bg-zinc-50 p-3 flex flex-wrap items-center justify-between gap-2" data-testid="task-gst-portal-panel">
                <div>
                  <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-500">GST portal</div>
                  <div className="text-xs text-zinc-700">Open gst.gov.in for {initial.client_name} — username auto-copied</div>
                </div>
                <GstPortalLaunch task={initial} vault={vault} isAdmin={user?.role === "admin"} compact tid="task-dialog-gst"
                  onChanged={() => api.get("/gst-vault/status").then(r => setVault(r.data)).catch(() => {})} />
              </div>
            ) : null}
            <div className="mt-4 border-t border-zinc-200 pt-4">
              <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-500 mb-3">Payment history</div>
              <PaymentHistory taskId={initial.id} />
            </div>
            <div className="mt-4 border-t border-zinc-200 pt-4">
              <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-500 mb-3">Attachments</div>
              <Attachments taskId={initial.id} clientId={initial.client_id} />
            </div>
          </>
        ) : (
          <div className="mt-2 text-[11px] text-zinc-500 border-t border-zinc-200 pt-3">
            Save the task first, then re-open it to see payment history and attach documents.
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="task-submit-btn" onClick={submit} disabled={busy} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-none">
            {busy ? "Saving…" : (isEdit ? "Save changes" : "Create task")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
